import {
  CONDITION_LABEL,
  DECISION_FOR_BUSINESS,
  DECISION_LABEL,
  type ConditionDisplay,
  type DecisionsView,
  type UnderwritingCondition,
} from '@cyberscore/contracts';
import { CalendarClock, Check, FileText, RotateCcw, Send, ShieldCheck, X } from 'lucide-react';
import { useState } from 'react';
import { useToast } from '../../ui/Toasts';
import { DropZone, uploadError } from '../documents/DocumentParts';
import { useUpload } from '../documents/api';
import { useDecisions, useReviewCondition, useSendEvidence } from './api';

const TONE: Record<ConditionDisplay, string> = {
  open: 'neutral',
  overdue: 'bad',
  evidenced: 'warn',
  cleared: 'ok',
  waived: 'neutral',
};

export const dueDate = (d: string) =>
  new Date(`${d}T00:00:00`).toLocaleDateString('en-MY', { day: 'numeric', month: 'short', year: 'numeric' });

/**
 * The insurer's decision and its conditions (task 64). The business sends evidence for each condition;
 * the underwriter clears it, sends it back with what is missing, or waives it.
 */
export function Conditions({ id, mode }: { id: string; mode: 'business' | 'staff' }) {
  const view = useDecisions(id);
  if (!view.data) return null;
  const v: DecisionsView = view.data;
  const latest = v.decisions.find((d) => d.outcome !== 'refer');
  if (!latest) return null;
  return (
    <section className={`card pad conditions-card ${latest.outcome}`} aria-labelledby={`decision-${id}`}>
      <div className="row-between">
        <h2 id={`decision-${id}`} className="section-title row gap">
          <ShieldCheck size={17} />
          {mode === 'business' ? 'Your insurer’s decision' : `Decision: ${DECISION_LABEL[latest.outcome]}`}
        </h2>
        {v.conditions.length > 0 && (
          <span className={`pill ${v.open_conditions ? 'warn' : 'ok'}`}>
            {v.open_conditions ? `${v.open_conditions} of ${v.conditions.length} open` : 'All conditions met'}
          </span>
        )}
      </div>
      <p className={mode === 'business' ? '' : 'muted small'}>
        {mode === 'business'
          ? DECISION_FOR_BUSINESS[latest.outcome as keyof typeof DECISION_FOR_BUSINESS]
          : `${latest.decided_by_name ?? 'An underwriter'} on ${new Date(latest.decided_at).toLocaleDateString('en-MY', { day: 'numeric', month: 'short', year: 'numeric' })}${latest.score ? `, on a CyberScore of ${latest.score.score}` : ''}. ${latest.reason ?? ''}`}
      </p>
      {v.conditions.length > 0 && (
        <ol className="conditions">
          {v.conditions.map((c) => (
            <ConditionItem key={c.id} id={id} c={c} mode={mode} />
          ))}
        </ol>
      )}
    </section>
  ); // prettier-ignore
}

function ConditionItem({
  id,
  c,
  mode,
}: {
  id: string;
  c: UnderwritingCondition;
  mode: 'business' | 'staff';
}) {
  const next = c.reminders.find((r) => r.status === 'scheduled');
  return (
    <li className={`condition ${c.display}`}>
      <div className="row-between top">
        <div className="grow">
          <div className="qtext">{c.title}</div>
          <div className="muted small row gap">
            <CalendarClock size={13} /> Due {dueDate(c.due_date)}
            {mode === 'staff' && next && ` · next reminder ${dueDate(next.send_on)}`}
            {c.control && ` · ${c.control}`}
          </div>
        </div>
        <span className={`pill ${TONE[c.display]}`}>{CONDITION_LABEL[c.display]}</span>
      </div>
      {c.detail && <p className="small">{c.detail}</p>}
      {c.review_note && (
        <p className="small request-note">
          <b>{c.state === 'waived' ? 'Waived:' : mode === 'business' ? 'Your insurer replied:' : 'Sent back:'}</b> {c.review_note}
        </p>
      )}
      {c.evidence_note && c.state !== 'open' && (
        <div className="small evidence">
          <b>{mode === 'business' ? 'You sent:' : 'Business sent:'}</b> {c.evidence_note}
          {c.evidence_documents.length > 0 && (
            <ul className="files">
              {c.evidence_documents.map((d) => (
                <li key={d.id}>
                  <FileText size={13} /> {d.filename}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      {mode === 'business' && c.state === 'open' && <EvidenceForm id={id} c={c} />}
      {mode === 'staff' && (c.state === 'open' || c.state === 'evidenced') && <ReviewButtons id={id} c={c} />}
    </li>
  ); // prettier-ignore
}

function EvidenceForm({ id, c }: { id: string; c: UnderwritingCondition }) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState('');
  const [files, setFiles] = useState<{ id: string; name: string }[]>([]);
  const upload = useUpload(id);
  const send = useSendEvidence(id);
  const toast = useToast();
  if (!open)
    return (
      <button type="button" className="btn btn-sm" onClick={() => setOpen(true)}>
        <Send size={13} /> Send evidence
      </button>
    );
  return (
    <form
      className="stack-sm mt"
      onSubmit={async (e) => {
        e.preventDefault();
        if (!note.trim()) return;
        try {
          await send.mutateAsync({ cid: c.id, body: { note: note.trim(), document_ids: files.map((f) => f.id) } });
          toast('Sent to your insurer', 'ok');
        } catch (err) {
          toast(err instanceof Error ? err.message : 'Could not send it', 'bad');
        }
      }}
    >
      <label className="small" htmlFor={`evidence-${c.id}`}>
        What did you do? Add a document if you have one (a policy, a screenshot, a report).
      </label>
      <textarea
        id={`evidence-${c.id}`}
        className="input"
        rows={2}
        maxLength={2000}
        value={note}
        autoFocus
        onChange={(e) => setNote(e.target.value)}
      />
      <DropZone
        busy={upload.isPending}
        label="Add a document"
        onFile={async (f) => {
          try {
            const r = await upload.mutateAsync({ file: f });
            setFiles((xs) => (xs.some((x) => x.id === r.document.id) ? xs : [...xs, { id: r.document.id, name: f.name }]));
          } catch (err) {
            toast(uploadError(err), 'bad');
          }
        }}
      />
      {files.length > 0 && (
        <ul className="files">
          {files.map((f) => (
            <li key={f.id}>
              <FileText size={13} /> {f.name}
              <button type="button" className="btn btn-sm btn-ghost btn-icon" aria-label={`Remove ${f.name}`} onClick={() => setFiles(files.filter((x) => x.id !== f.id))}>
                <X size={13} />
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="row gap">
        <button type="submit" className="btn btn-sm btn-primary" disabled={!note.trim() || send.isPending || upload.isPending}>
          Send to insurer
        </button>
        <button type="button" className="btn btn-sm btn-ghost" onClick={() => setOpen(false)}>
          Cancel
        </button>
      </div>
    </form>
  ); // prettier-ignore
}

function ReviewButtons({ id, c }: { id: string; c: UnderwritingCondition }) {
  const review = useReviewCondition(id);
  const toast = useToast();
  const [asking, setAsking] = useState<null | 'reopen' | 'waive'>(null);
  const [note, setNote] = useState('');
  const act = async (action: 'clear' | 'reopen' | 'waive', n?: string) => {
    try {
      await review.mutateAsync({ cid: c.id, body: { action, note: n } });
      setAsking(null);
      setNote('');
    } catch (err) {
      toast(err instanceof Error ? err.message : 'That didn’t work', 'bad');
    }
  };
  return (
    <>
      <div className="row gap wrap">
        {c.state === 'evidenced' && (
          <>
            <button type="button" className="btn btn-sm" disabled={review.isPending} onClick={() => act('clear')}>
              <Check size={13} /> Condition met
            </button>
            <button type="button" className="btn btn-sm btn-ghost" disabled={review.isPending} onClick={() => setAsking('reopen')}>
              <RotateCcw size={13} /> Send back
            </button>
          </>
        )}
        <button type="button" className="btn btn-sm btn-ghost" disabled={review.isPending} onClick={() => setAsking('waive')}>
          Waive
        </button>
      </div>
      {asking && (
        <form
          className="stack-sm mt"
          onSubmit={(e) => {
            e.preventDefault();
            if (note.trim()) void act(asking, note.trim());
          }}
        >
          <label className="small" htmlFor={`cond-note-${c.id}`}>
            {asking === 'reopen' ? 'What is still missing? The business sees this.' : 'Why waive it?'}
          </label>
          <textarea id={`cond-note-${c.id}`} className="input" rows={2} maxLength={2000} value={note} autoFocus onChange={(e) => setNote(e.target.value)} />
          <div className="row gap">
            <button type="submit" className="btn btn-sm btn-primary" disabled={!note.trim() || review.isPending}>
              {asking === 'reopen' ? 'Send it back' : 'Waive it'}
            </button>
            <button type="button" className="btn btn-sm btn-ghost" onClick={() => setAsking(null)}>
              Cancel
            </button>
          </div>
        </form>
      )}
    </>
  ); // prettier-ignore
}
